import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ListsManager from '../services/ListsManager';
import { sentimentToPct, pctToSentiment } from '../lib/utils';
import { Send, Eye, Trash2, ArrowRightLeft, ChevronDown, ChevronUp, Clock, FileText, X, Plus, ExternalLink, Link, Check, Loader, AlertCircle, Tv } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import DailyReportView, { REPORT_STYLES } from '../components/DailyReportView';

const PLATFORM_LABELS = {
  twitter: 'X',
  tiktok: 'TikTok',
  instagram: 'Instagram',
  telegram: 'Telegram',
  facebook: 'Facebook',
  rss: 'Web',
  webz: 'News',
  tv: 'TV',
};

const PLATFORM_ICONS = {
  twitter: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="#000"/>
    </svg>
  ),
  tiktok: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.88 2.89 2.89 0 01-2.88-2.88 2.89 2.89 0 012.88-2.88c.28 0 .56.04.82.1V9.4a6.28 6.28 0 00-.82-.05A6.34 6.34 0 003.15 15.7 6.34 6.34 0 009.49 22a6.34 6.34 0 006.34-6.34V9.01a8.24 8.24 0 004.76 1.5V7.1a4.83 4.83 0 01-1-.41z" fill="#fe2c55"/>
    </svg>
  ),
  instagram: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <rect x="2" y="2" width="20" height="20" rx="5" stroke="url(#ig)" strokeWidth="2"/>
      <circle cx="12" cy="12" r="5" stroke="url(#ig)" strokeWidth="2"/>
      <circle cx="17.5" cy="6.5" r="1.5" fill="url(#ig)"/>
      <defs><linearGradient id="ig" x1="2" y1="22" x2="22" y2="2"><stop stopColor="#f09433"/><stop offset=".5" stopColor="#dc2743"/><stop offset="1" stopColor="#bc1888"/></linearGradient></defs>
    </svg>
  ),
  telegram: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" fill="#34aadf"/>
    </svg>
  ),
  rss: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle cx="6.18" cy="17.82" r="2.18" fill="#ff6600"/>
      <path d="M4 4.44v2.83c7.03 0 12.73 5.7 12.73 12.73h2.83c0-8.59-6.97-15.56-15.56-15.56zm0 5.66v2.83c3.9 0 7.07 3.17 7.07 7.07h2.83c0-5.47-4.43-9.9-9.9-9.9z" fill="#ff6600"/>
    </svg>
  ),
  webz: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="#1a73e8" strokeWidth="2"/>
      <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" stroke="#1a73e8" strokeWidth="2"/>
    </svg>
  ),
  facebook: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877f2"/>
    </svg>
  ),
  tv: (<Tv size={16} color="#9f1239" />),
};

const PLATFORM_COLORS = {
  twitter: { accent: '#1d9bf0', bg: '#ffffff', border: '#cfd9de' },
  tiktok: { accent: '#fe2c55', bg: '#ffffff', border: '#fee2e2' },
  instagram: { accent: '#e4405f', bg: '#ffffff', border: '#fce7f3' },
  telegram: { accent: '#34aadf', bg: '#edf7ff', border: '#bae6fd' },
  rss: { accent: '#ff6600', bg: '#ffffff', border: '#fed7aa' },
  webz: { accent: '#1a73e8', bg: '#ffffff', border: '#bfdbfe' },
  facebook: { accent: '#1877f2', bg: '#f0e8df', border: '#e2e8f0' },
  tv: { accent: '#9f1239', bg: '#ffffff', border: '#fecdd3' },
};

const formatNumber = (num) => {
  if (num == null) return null;
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toLocaleString();
};

const PostCard = ({ post, orgLists, currentListId, onRemove, onMove, onUpdateField, isRemoving, movedListId, hideMove }) => {
  const [showMoveDropdown, setShowMoveDropdown] = useState(false);
  const [editingSummary, setEditingSummary] = useState(false);
  const [editingReason, setEditingReason] = useState(false);
  const [editingSentiment, setEditingSentiment] = useState(false);
  const [localSummary, setLocalSummary] = useState(post.summary || '');
  const [localReason, setLocalReason] = useState(post.reason || '');
  const [localSentiment, setLocalSentiment] = useState('');
  const dropdownRef = useRef(null);

  const source = post.source || 'twitter';
  const colors = PLATFORM_COLORS[source] || PLATFORM_COLORS.facebook;

  const availableLists = orgLists.filter(list => list.id !== (currentListId || post.list_id));

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowMoveDropdown(false);
      }
    };
    if (showMoveDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMoveDropdown]);

  const editListId = post.list_id || currentListId;

  const handleSaveSummary = () => {
    if (localSummary !== post.summary) {
      onUpdateField(post.platform_id, post.source, { summary: localSummary }, editListId);
    }
    setEditingSummary(false);
  };

  const handleSaveReason = () => {
    if (localReason !== post.reason) {
      onUpdateField(post.platform_id, post.source, { reason: localReason }, editListId);
    }
    setEditingReason(false);
  };

  const handleSaveSentiment = () => {
    const raw = pctToSentiment(localSentiment);
    if (raw !== post.sentiment_score) {
      onUpdateField(post.platform_id, post.source, { sentiment_score: raw }, editListId);
    }
    setEditingSentiment(false);
  };

  if (movedListId && movedListId !== currentListId) return null;

  const sentimentScore = sentimentToPct(post.sentiment_score);
  const sentimentStyle = sentimentScore == null || (sentimentScore <= 20 && sentimentScore >= -20)
    ? { backgroundColor: '#fef3c7', color: '#d97706' }
    : sentimentScore > 20
      ? { backgroundColor: '#dcfce7', color: '#16a34a' }
      : { backgroundColor: '#fee2e2', color: '#dc2626' };

  return (
    <motion.div
      layout
      initial={{ opacity: 1, scale: 1 }}
      animate={{ opacity: isRemoving ? 0 : 1, scale: isRemoving ? 0.95 : 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className={`post-card post-card--${source}`}
    >
      {source === 'instagram' && <div className="post-card__ig-bar" />}

      <div className="post-card__header">
        <div className="post-card__author">
          {post.author_profile_image_url ? (
            <img
              src={post.author_profile_image_url}
              alt=""
              className="post-card__avatar"
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
          ) : null}
          <div
            className="post-card__avatar-fallback"
            style={{
              backgroundColor: colors.accent,
              display: post.author_profile_image_url ? 'none' : 'flex'
            }}
          >
            {(post.author_name || '?')[0].toUpperCase()}
          </div>
          <div className="post-card__author-info">
            <span className="post-card__name">{post.author_name || 'Unknown'}</span>
            {post.author_username && (
              <span className="post-card__handle">@{post.author_username}</span>
            )}
          </div>
          {post.followers_count > 0 && (
            <span className="post-card__followers">
              {formatNumber(post.followers_count)} עוקבים
            </span>
          )}
          <span className="post-card__platform-icon">
            {PLATFORM_ICONS[source]}
          </span>
        </div>

        <div className="post-card__actions">
          {post.post_url && (
            <a href={post.post_url} target="_blank" rel="noopener noreferrer" className="post-card__btn post-card__btn--link" title="פתח מקור">
              <ExternalLink size={14} />
            </a>
          )}
          {!hideMove && (
          <div style={{ position: 'relative' }} ref={dropdownRef}>
            <button className="post-card__btn post-card__btn--move" onClick={() => setShowMoveDropdown(!showMoveDropdown)}>
              <ArrowRightLeft size={14} />
            </button>
            {showMoveDropdown && availableLists.length > 0 && (
              <div className="post-card__dropdown">
                {availableLists.map(list => (
                  <button
                    key={list.id}
                    className="post-card__dropdown-item"
                    onClick={() => {
                      onMove(post.platform_id, post.source, currentListId || post.list_id, list.id);
                      setShowMoveDropdown(false);
                    }}
                  >
                    {list.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          )}
          <button className="post-card__btn post-card__btn--remove" onClick={() => onRemove(post.platform_id, post.source)}>
            <X size={14} />
          </button>
        </div>
      </div>

      {post.post_url ? (
        <a href={post.post_url} target="_blank" rel="noopener noreferrer" className="post-card__link">
          <div className="post-card__content" dir="rtl">
            {post.content}
          </div>
          {source === 'tv' && post.video_url ? (
            <div className="post-card__media" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
              <video src={post.video_url} controls playsInline style={{ width: '100%', aspectRatio: '16/9', borderRadius: 8, display: 'block', background: '#000' }} />
            </div>
          ) : post.media_url ? (
            <div className="post-card__media">
              <img src={post.media_url} alt="" loading="lazy" />
            </div>
          ) : null}
        </a>
      ) : (
        <>
          <div className="post-card__content" dir="rtl">
            {post.content}
          </div>
          {source === 'tv' && post.video_url ? (
            <div className="post-card__media" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
              <video src={post.video_url} controls playsInline style={{ width: '100%', aspectRatio: '16/9', borderRadius: 8, display: 'block', background: '#000' }} />
            </div>
          ) : post.media_url ? (
            <div className="post-card__media">
              <img src={post.media_url} alt="" loading="lazy" />
            </div>
          ) : null}
        </>
      )}

      <div className="post-card__footer">
        <div className="post-card__stats">
          {post.likes != null && (
            <span className="post-card__stat">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
              {formatNumber(post.likes)}
            </span>
          )}
          {post.comments != null && (
            <span className="post-card__stat">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
              {formatNumber(post.comments)}
            </span>
          )}
          {post.views != null && (
            <span className="post-card__stat">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              {formatNumber(post.views)}
            </span>
          )}
          {post.post_time && (
            <span className="post-card__time">{post.post_time}</span>
          )}
        </div>
        {editingSentiment ? (
          <input
            type="number"
            min={-100}
            max={100}
            className="post-card__sentiment-input"
            value={localSentiment}
            autoFocus
            onChange={(e) => setLocalSentiment(e.target.value)}
            onBlur={handleSaveSentiment}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveSentiment();
              if (e.key === 'Escape') setEditingSentiment(false);
            }}
            dir="ltr"
          />
        ) : (
          <span
            className="post-card__sentiment"
            style={{ ...sentimentStyle, cursor: 'pointer' }}
            title="ערוך סנטימנט"
            onClick={() => {
              setLocalSentiment(String(sentimentScore ?? 0));
              setEditingSentiment(true);
            }}
          >
            {sentimentScore == null ? '± קבע' : `${sentimentScore > 0 ? '+' : ''}${sentimentScore}`}
          </span>
        )}
      </div>

      {editingSummary ? (
        <div className="post-card__insight">
          <div className="post-card__insight-label">סיכום</div>
          <textarea
            className="post-card__insight-input"
            value={localSummary}
            onChange={(e) => setLocalSummary(e.target.value)}
            onBlur={handleSaveSummary}
            autoFocus
            dir="rtl"
          />
        </div>
      ) : post.summary ? (
        <div className="post-card__insight post-card__insight--clickable" onClick={() => setEditingSummary(true)}>
          <div className="post-card__insight-label">סיכום</div>
          <div className="post-card__insight-text" dir="rtl">{post.summary}</div>
        </div>
      ) : null}

      {editingReason ? (
        <div className="post-card__insight">
          <div className="post-card__insight-label">סיבה</div>
          <textarea
            className="post-card__insight-input"
            value={localReason}
            onChange={(e) => setLocalReason(e.target.value)}
            onBlur={handleSaveReason}
            autoFocus
            dir="rtl"
          />
        </div>
      ) : post.reason ? (
        <div className="post-card__insight post-card__insight--clickable" onClick={() => setEditingReason(true)}>
          <div className="post-card__insight-label">סיבה</div>
          <div className="post-card__insight-text" dir="rtl">{post.reason}</div>
        </div>
      ) : null}

      {source === 'twitter' && Array.isArray(post.dependencies) && post.dependencies.length > 0 && (
        <div className="post-card__deps">
          {post.dependencies.map((dep, i) => {
            if (typeof dep !== 'object' || dep === null) return null;
            const depName = dep.name || dep.username || '';
            const isRtl = dep.content ? /[֐-׿؀-ۿ]/.test(dep.content) : true;
            return (
              <div key={i} className="post-dep">
                {dep.relation_label && (
                  <div className="post-dep__rel">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M15 17l5-5-5-5"/><path d="M20 12H9a4 4 0 00-4 4v2"/></svg>
                    {dep.relation_label}{dep.username ? ` @${dep.username}` : ''}
                  </div>
                )}
                {dep.username && (
                  <div className="post-dep__head">
                    {dep.profile_image_url ? (
                      <img className="post-dep__avatar-img" src={dep.profile_image_url} alt=""
                        onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <span className="post-dep__avatar">{(depName || '?')[0].toUpperCase()}</span>
                    )}
                    <span className="post-dep__name">{depName}</span>
                    <span className="post-dep__handle">@{dep.username}</span>
                  </div>
                )}
                {dep.content && (
                  <div className="post-dep__body" dir={isRtl ? 'rtl' : 'ltr'}>
                    {dep.content.slice(0, 200)}{dep.content.length > 200 ? '…' : ''}
                  </div>
                )}
                {dep.url && (
                  <a className="post-dep__open" href={dep.url} target="_blank" rel="noopener noreferrer">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    פתח את הציוץ המקורי
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};

const Sentiment30DChart = ({ sentiment30d, sentiment30dTotals }) => {
  if (!sentiment30d || sentiment30d.length === 0) return null;

  const totals = sentiment30dTotals || {};
  const total = totals.total || 0;
  const posPct = total > 0 ? Math.round((totals.positive || 0) / total * 100) : 0;
  const neuPct = total > 0 ? Math.round((totals.neutral || 0) / total * 100) : 0;
  const negPct = total > 0 ? Math.round((totals.negative || 0) / total * 100) : 0;

  return (
    <div className="dr-section" style={{ marginBottom: '24px' }}>
      <h2 className="dr-section__title" style={{ marginBottom: '16px' }}>סנטימנט מצטבר — 30 ימים</h2>
      <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '100px', direction: 'ltr', marginBottom: '6px' }}>
          {sentiment30d.map((day, i) => {
            const dayTotal = day.positive + day.neutral + day.negative;
            return (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%', borderRadius: '3px 3px 0 0', overflow: 'hidden' }}>
                {dayTotal > 0 ? (
                  <>
                    {day.negative > 0 && <div style={{ height: `${(day.negative / dayTotal * 100).toFixed(1)}%`, backgroundColor: '#dc2626' }} />}
                    {day.neutral > 0 && <div style={{ height: `${(day.neutral / dayTotal * 100).toFixed(1)}%`, backgroundColor: '#d97706' }} />}
                    {day.positive > 0 && <div style={{ height: `${(day.positive / dayTotal * 100).toFixed(1)}%`, backgroundColor: '#16a34a' }} />}
                  </>
                ) : (
                  <div style={{ height: '100%', backgroundColor: '#e2e8f0' }} />
                )}
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: '3px', direction: 'ltr', marginBottom: '12px' }}>
          {sentiment30d.map((day, i) => (
            <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: '9px', color: '#94a3b8', overflow: 'hidden' }}>
              {i % 2 === 0 ? day.day : ''}
            </div>
          ))}
        </div>
        {total > 0 && (
          <>
            <div style={{ display: 'flex', height: '20px', borderRadius: '10px', overflow: 'hidden', direction: 'ltr' }}>
              {negPct > 0 && <div style={{ width: `${negPct}%`, backgroundColor: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, color: '#fff' }}>{negPct}%</div>}
              {neuPct > 0 && <div style={{ width: `${neuPct}%`, backgroundColor: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, color: '#fff' }}>{neuPct}%</div>}
              {posPct > 0 && <div style={{ width: `${posPct}%`, backgroundColor: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, color: '#fff' }}>{posPct}%</div>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '8px', fontSize: '13px', color: '#5a6178', direction: 'rtl' }}>
              <span><span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#16a34a', marginLeft: '4px', verticalAlign: 'middle' }} />חיובי {posPct}% ({totals.positive || 0})</span>
              <span><span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#d97706', marginLeft: '4px', verticalAlign: 'middle' }} />ניטרלי {neuPct}% ({totals.neutral || 0})</span>
              <span><span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#dc2626', marginLeft: '4px', verticalAlign: 'middle' }} />שלילי {negPct}% ({totals.negative || 0})</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const DailyReviewScreen = () => {
  const { approvalId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingReports, setPendingReports] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [summaryBullets, setSummaryBullets] = useState([]);
  const [focusedBulletIndex, setFocusedBulletIndex] = useState(null);
  const [connectTheDots, setConnectTheDots] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [removingPosts, setRemovingPosts] = useState({});
  const [movedPosts, setMovedPosts] = useState({});
  const [removedCount, setRemovedCount] = useState(0);
  const [countdown, setCountdown] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [previewingReport, setPreviewingReport] = useState(false);
  const [previewData, setPreviewData] = useState(null);   // web preview of the member report
  const [deliveryMode, setDeliveryMode] = useState('link'); // backend REPORTS_DELIVERY_MODE
  const [sentiment30d, setSentiment30d] = useState([]);
  const [sentiment30dTotals, setSentiment30dTotals] = useState({});
  const [articleUrl, setArticleUrl] = useState('');
  const [articleListId, setArticleListId] = useState('');
  const [articleQueue, setArticleQueue] = useState([]);
  const [articleSectionOpen, setArticleSectionOpen] = useState(false);
  const articleProcessingRef = useRef(false);

  const saveTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const connectTheDotsRef = useRef(null);
  const ctdSaveTimeoutRef = useRef(null);
  const summaryBulletsRef = useRef(null);

  // Keep a live ref to the latest connect-the-dots so the debounced save never
  // persists a stale closure value.
  useEffect(() => {
    connectTheDotsRef.current = connectTheDots;
  }, [connectTheDots]);

  useEffect(() => {
    summaryBulletsRef.current = summaryBullets;
  }, [summaryBullets]);

  // Flush any pending debounced summary / connect-the-dots saves and persist the latest
  // values NOW. Called before preview/send so a last-second edit (within the 1s debounce)
  // isn't lost — this was the cause of "I edited/removed a dot and nothing happened".
  const flushPendingSaves = useCallback(async () => {
    if (ctdSaveTimeoutRef.current) {
      clearTimeout(ctdSaveTimeoutRef.current);
      ctdSaveTimeoutRef.current = null;
    }
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    try {
      if (connectTheDotsRef.current !== null) {
        await ListsManager.updatePresendConnectTheDots(approvalId, connectTheDotsRef.current);
      }
      if (Array.isArray(summaryBulletsRef.current)) {
        await ListsManager.updatePresendSummary(approvalId, summaryBulletsRef.current.filter(b => b.trim()));
      }
    } catch (err) {
      console.error('Failed to flush pending saves:', err);
    }
  }, [approvalId]);

  useEffect(() => {
    if (approvalId) {
      loadReportData();
    } else {
      loadPendingReports();
    }
  }, [approvalId]);

  useEffect(() => {
    if (reportData) {
      startCountdown();
    }
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [reportData]);

  const loadPendingReports = async () => {
    try {
      setLoading(true);
      const result = await ListsManager.getPresendReports();
      setPendingReports(result.approvals || []);
    } catch (err) {
      setError('Failed to load pending reports');
    } finally {
      setLoading(false);
    }
  };

  const loadReportData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ListsManager.getPresendReport(approvalId);

      if (!data || data.status !== 'pending') {
        setError('Report not found or already sent');
        setLoading(false);
        return;
      }

      setReportData(data);
      setDeliveryMode(data.delivery_mode || 'link');

      if (data.report_data?.sentiment_30d) {
        setSentiment30d(JSON.parse(JSON.stringify(data.report_data.sentiment_30d)));
      }
      if (data.report_data?.sentiment_30d_totals) {
        setSentiment30dTotals(JSON.parse(JSON.stringify(data.report_data.sentiment_30d_totals)));
      }

      if (data.report_data?.summary_text) {
        const bullets = Array.isArray(data.report_data.summary_text)
          ? data.report_data.summary_text
          : [data.report_data.summary_text];
        setSummaryBullets(bullets);
      } else {
        setSummaryBullets(['']);
      }

      setConnectTheDots(
        data.report_data?.connect_the_dots
          ? JSON.parse(JSON.stringify(data.report_data.connect_the_dots))
          : null
      );
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const startCountdown = () => {
    const updateCountdown = () => {
      const now = new Date();
      const israelTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Jerusalem' }));

      const target = new Date(israelTime);
      target.setHours(8, 0, 0, 0);
      if (israelTime >= target) {
        target.setDate(target.getDate() + 1);
      }

      const diff = target - israelTime;

      if (diff <= 0) {
        setCountdown('הדו״ח נשלח אוטומטית');
        setIsUrgent(false);
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
      setIsUrgent(diff < 5 * 60 * 1000);
    };

    updateCountdown();
    countdownIntervalRef.current = setInterval(updateCountdown, 1000);
  };

  const debouncedSaveSummary = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await ListsManager.updatePresendSummary(approvalId, summaryBullets.filter(b => b.trim()));
      } catch (err) {
        console.error('Failed to save summary:', err);
      }
    }, 1000);
  }, [approvalId, summaryBullets]);

  const handleBulletChange = useCallback((index, value) => {
    setSummaryBullets(prev => {
      const newBullets = [...prev];
      newBullets[index] = value;
      return newBullets;
    });
  }, []);

  const handleBulletBlur = useCallback((index) => {
    setFocusedBulletIndex(null);
    if (!summaryBullets[index].trim()) {
      setSummaryBullets(prev => prev.filter((_, i) => i !== index || prev.length === 1));
    }
    debouncedSaveSummary();
  }, [summaryBullets, debouncedSaveSummary]);

  const handleRemoveBullet = useCallback((index) => {
    setSummaryBullets(prev => {
      if (prev.length <= 1) return prev;
      const newBullets = prev.filter((_, i) => i !== index);
      return newBullets;
    });
    debouncedSaveSummary();
  }, [debouncedSaveSummary]);

  const handleAddBullet = useCallback(() => {
    setSummaryBullets(prev => [...prev, '']);
    setTimeout(() => {
      setFocusedBulletIndex(summaryBullets.length);
    }, 50);
  }, [summaryBullets.length]);

  // ── Connect-the-dots editing (persisted to DB via the backend) ──
  const debouncedSaveCTD = useCallback(() => {
    if (ctdSaveTimeoutRef.current) clearTimeout(ctdSaveTimeoutRef.current);
    ctdSaveTimeoutRef.current = setTimeout(async () => {
      try {
        await ListsManager.updatePresendConnectTheDots(approvalId, connectTheDotsRef.current);
      } catch (err) {
        console.error('Failed to save connect-the-dots:', err);
      }
    }, 1000);
  }, [approvalId]);

  // Apply a mutation to a deep copy of the CTD block, then persist (debounced).
  const updateCtd = useCallback((mutator) => {
    setConnectTheDots(prev => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      mutator(next);
      return next;
    });
    debouncedSaveCTD();
  }, [debouncedSaveCTD]);

  const updateCtdHeadline = useCallback((value) => {
    updateCtd(d => { d.headline = value; });
  }, [updateCtd]);

  const updateCtdEventField = useCallback((index, field, value) => {
    updateCtd(d => { if (d.today_events?.[index]) d.today_events[index][field] = value; });
  }, [updateCtd]);

  const removeCtdEvent = useCallback((index) => {
    updateCtd(d => { if (d.today_events) d.today_events.splice(index, 1); });
  }, [updateCtd]);

  const updateCtdConnectionField = useCallback((index, field, value) => {
    updateCtd(d => { if (d.connections?.[index]) d.connections[index][field] = value; });
  }, [updateCtd]);

  const removeCtdConnection = useCallback((index) => {
    updateCtd(d => { if (d.connections) d.connections.splice(index, 1); });
  }, [updateCtd]);

  const handleRemovePost = useCallback(async (platformId, source) => {
    const key = `${platformId}-${source}`;
    setRemovingPosts(prev => ({ ...prev, [key]: true }));

    // Find post's sentiment before removal for chart update, and the list it belongs to so
    // the backend only removes this list's copy (the same URL can be in several lists).
    let removedSentiment = null;
    let removedListId = null;
    if (reportData?.report_data) {
      const allPosts = [];
      (reportData.report_data.top_mentions || []).forEach(p => allPosts.push(p));
      (reportData.report_data.hourly_posts_by_list || []).forEach(ls => {
        (ls.hours || []).forEach(h => (h.posts || []).forEach(p => {
          allPosts.push(p);
          if (`${p.platform_id}-${p.source}` === key && removedListId == null) {
            removedListId = p.list_id || ls.list_id || null;
          }
        }));
      });
      const found = allPosts.find(p => `${p.platform_id}-${p.source}` === key);
      if (found) removedSentiment = found.sentiment_score;
    }

    try {
      const ok = await ListsManager.presendRemovePost(approvalId, platformId, source, removedListId);
      if (!ok) throw new Error('Failed');
      setRemovedCount(prev => prev + 1);

      // Update 30-day sentiment chart
      setSentiment30d(prev => {
        if (!prev.length) return prev;
        const updated = [...prev];
        const lastDay = { ...updated[updated.length - 1] };
        let cat = 'neutral';
        if (removedSentiment != null) {
          if (removedSentiment > 0.2) cat = 'positive';
          else if (removedSentiment < -0.2) cat = 'negative';
        }
        lastDay[cat] = Math.max(0, (lastDay[cat] || 0) - 1);
        updated[updated.length - 1] = lastDay;
        return updated;
      });
      setSentiment30dTotals(prev => {
        let cat = 'neutral';
        if (removedSentiment != null) {
          if (removedSentiment > 0.2) cat = 'positive';
          else if (removedSentiment < -0.2) cat = 'negative';
        }
        return {
          ...prev,
          [cat]: Math.max(0, (prev[cat] || 0) - 1),
          total: Math.max(0, (prev.total || 0) - 1),
        };
      });
      setTimeout(() => {
        setReportData(prev => {
          if (!prev) return prev;
          const newData = { ...prev, report_data: { ...prev.report_data } };
          if (newData.report_data?.hourly_posts_by_list) {
            newData.report_data.hourly_posts_by_list = newData.report_data.hourly_posts_by_list.map(section => ({
              ...section,
              hours: (section.hours || []).map(h => ({
                ...h,
                posts: (h.posts || []).filter(p => `${p.platform_id}-${p.source}` !== key)
              })).filter(h => h.posts && h.posts.length > 0)
            })).filter(s => s.hours && s.hours.length > 0);
          }
          if (newData.report_data?.top_mentions) {
            newData.report_data.top_mentions = newData.report_data.top_mentions.filter(p => `${p.platform_id}-${p.source}` !== key);
          }
          const countsKey = ['twitter', 'tiktok', 'instagram', 'telegram', 'facebook_group'].includes(source) ? source : 'web';
          const counts = { ...(newData.report_data.platform_counts || {}) };
          if (counts[countsKey] > 0) counts[countsKey]--;
          if (counts.total > 0) counts.total--;
          newData.report_data.platform_counts = counts;
          return newData;
        });
        setRemovingPosts(prev => {
          const newState = { ...prev };
          delete newState[key];
          return newState;
        });
      }, 200);
    } catch (err) {
      setRemovingPosts(prev => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
      console.error('Failed to remove post:', err);
    }
  }, [approvalId, reportData]);

  const handleMovePost = useCallback(async (platformId, source, fromListId, targetListId) => {
    const key = `${platformId}-${source}`;

    try {
      const ok = await ListsManager.presendMovePost(approvalId, platformId, source, fromListId, targetListId);
      if (!ok) throw new Error('Failed');
      setMovedPosts(prev => ({ ...prev, [key]: targetListId }));

      setTimeout(() => {
        setReportData(prev => {
          if (!prev) return prev;
          const newData = { ...prev, report_data: { ...prev.report_data } };

          // Find the post being moved
          let movedPost = null;
          if (newData.report_data?.top_mentions) {
            movedPost = newData.report_data.top_mentions.find(p => `${p.platform_id}-${p.source}` === key);
            newData.report_data.top_mentions = newData.report_data.top_mentions.filter(p => `${p.platform_id}-${p.source}` !== key);
          }

          if (newData.report_data?.hourly_posts_by_list) {
            if (!movedPost) {
              for (const section of newData.report_data.hourly_posts_by_list) {
                for (const h of (section.hours || [])) {
                  const found = (h.posts || []).find(p => `${p.platform_id}-${p.source}` === key);
                  if (found) { movedPost = found; break; }
                }
                if (movedPost) break;
              }
            }

            // Remove from source sections
            newData.report_data.hourly_posts_by_list = newData.report_data.hourly_posts_by_list.map(section => ({
              ...section,
              hours: (section.hours || []).map(h => ({
                ...h,
                posts: (h.posts || []).filter(p => `${p.platform_id}-${p.source}` !== key)
              })).filter(h => h.posts && h.posts.length > 0)
            }));

            // Add to destination section
            if (movedPost) {
              const updatedPost = { ...movedPost, list_id: targetListId };
              let targetSection = newData.report_data.hourly_posts_by_list.find(s => s.list_id === targetListId);
              if (targetSection) {
                const hourLabel = movedPost.post_time ? movedPost.post_time.split(':')[0] + ':00' : '?';
                let targetHour = targetSection.hours.find(h => h.label === hourLabel);
                if (targetHour) {
                  targetHour.posts = [...targetHour.posts, updatedPost];
                } else {
                  targetSection.hours = [...targetSection.hours, { label: hourLabel, posts: [updatedPost] }];
                  targetSection.hours.sort((a, b) => b.label.localeCompare(a.label));
                }
              } else {
                const targetList = (prev.org_lists || []).find(l => l.id === targetListId);
                newData.report_data.hourly_posts_by_list.push({
                  list_id: targetListId,
                  list_name: targetList ? targetList.name : '',
                  hours: [{ label: movedPost.post_time ? movedPost.post_time.split(':')[0] + ':00' : '?', posts: [updatedPost] }],
                });
              }
            }

            // Clean up empty source sections
            newData.report_data.hourly_posts_by_list = newData.report_data.hourly_posts_by_list.filter(s => s.hours && s.hours.length > 0);
          }

          return newData;
        });
        setMovedPosts(prev => {
          const newState = { ...prev };
          delete newState[key];
          return newState;
        });
      }, 300);
    } catch (err) {
      console.error('Failed to move post:', err);
    }
  }, [approvalId]);

  const handleUpdateField = useCallback(async (platformId, source, fields, listId) => {
    try {
      const entries = Object.entries(fields);
      for (const [field, value] of entries) {
        await ListsManager.presendEditField(approvalId, platformId, source, field, value, listId);
      }
      setReportData(prev => {
        if (!prev) return prev;
        const newData = { ...prev };
        const updatePosts = (posts) => posts.map(p =>
          p.platform_id === platformId && p.source === source
            ? { ...p, ...fields }
            : p
        );
        if (newData.report_data?.hourly_posts_by_list) {
          newData.report_data.hourly_posts_by_list = newData.report_data.hourly_posts_by_list.map(section => ({
            ...section,
            hours: (section.hours || []).map(h => ({
              ...h,
              posts: updatePosts(h.posts || [])
            }))
          }));
        }
        if (newData.report_data?.top_mentions) {
          newData.report_data.top_mentions = updatePosts(newData.report_data.top_mentions);
        }
        return newData;
      });
    } catch (err) {
      console.error('Failed to update field:', err);
    }
  }, [approvalId]);

  // Preview whatever the members will ACTUALLY receive, as decided by the backend's
  // REPORTS_DELIVERY_MODE (config.py):
  //   'pdf'          → the PDF, as before
  //   'link'/'both'  → the standalone web report (the page the emailed link opens)
  // Re-fetching the presend report runs the same assemble_presend_report_data the send
  // path runs, so either preview is WYSIWYG.
  const handlePreviewReport = useCallback(async () => {
    setPreviewingReport(true);
    try {
      await flushPendingSaves();

      if (deliveryMode === 'pdf') {
        const blob = await ListsManager.previewPresendReport(approvalId);
        if (!blob) {
          alert('Failed to generate preview');
          return;
        }
        window.open(URL.createObjectURL(blob), '_blank');
        return;
      }

      const data = await ListsManager.getPresendReport(approvalId);
      if (!data || !data.report_data) {
        alert('לא ניתן לטעון את התצוגה המקדימה');
        return;
      }
      setPreviewData(data);
    } catch (err) {
      console.error('Failed to preview report:', err);
      alert('לא ניתן לטעון את התצוגה המקדימה');
    } finally {
      setPreviewingReport(false);
    }
  }, [approvalId, flushPendingSaves, deliveryMode]);

  const handleSendReport = useCallback(async () => {
    if (!window.confirm('לשלוח את הדו״ח לכל חברי הארגון?')) {
      return;
    }

    setSendingReport(true);
    try {
      await flushPendingSaves();
      const result = await ListsManager.sendPresendReport(approvalId);
      if (result.success) {
        alert(result.message || 'הדו״ח נשלח בהצלחה!');
        navigate('/daily-review');
      } else {
        alert(result.detail || 'אירעה שגיאה בשליחת הדו״ח');
      }
    } catch (err) {
      console.error('Failed to send report:', err);
      alert('שגיאת רשת — הדו״ח לא נשלח. נסה שוב.');
    } finally {
      setSendingReport(false);
    }
  }, [approvalId, navigate, flushPendingSaves]);

  const handleAddArticle = useCallback(() => {
    if (!articleUrl.trim() || !articleListId) return;
    const newItem = {
      id: Date.now().toString(),
      url: articleUrl.trim(),
      listId: articleListId,
      listName: (reportData?.org_lists || []).find(l => l.id === articleListId)?.name || '',
      status: 'pending',
    };
    setArticleQueue(prev => [...prev, newItem]);
    setArticleUrl('');
  }, [articleUrl, articleListId, reportData]);

  const addPostToReportData = useCallback((post, listId, listName) => {
    setReportData(prev => {
      if (!prev) return prev;
      const newData = { ...prev, report_data: { ...prev.report_data } };
      const hourlyPosts = [...(newData.report_data.hourly_posts_by_list || [])];
      const hourLabel = post.post_time ? post.post_time.split(':')[0] + ':00' : 'ידני';

      const existingIdx = hourlyPosts.findIndex(s => s.list_id === listId);
      if (existingIdx >= 0) {
        const section = { ...hourlyPosts[existingIdx], hours: [...(hourlyPosts[existingIdx].hours || [])] };
        const hourIdx = section.hours.findIndex(h => h.label === hourLabel);
        if (hourIdx >= 0) {
          section.hours[hourIdx] = { ...section.hours[hourIdx], posts: [...section.hours[hourIdx].posts, post] };
        } else {
          section.hours = [{ label: hourLabel, posts: [post] }, ...section.hours];
        }
        hourlyPosts[existingIdx] = section;
      } else {
        hourlyPosts.push({
          list_id: listId,
          list_name: listName,
          hours: [{ label: hourLabel, posts: [post] }],
        });
      }
      newData.report_data.hourly_posts_by_list = hourlyPosts;

      const counts = { ...(newData.report_data.platform_counts || {}) };
      counts.webz = (counts.webz || 0) + 1;
      counts.total = (counts.total || 0) + 1;
      newData.report_data.platform_counts = counts;
      return newData;
    });
  }, []);

  useEffect(() => {
    const processNext = async () => {
      if (articleProcessingRef.current) return;
      const pending = articleQueue.find(item => item.status === 'pending');
      if (!pending) return;

      articleProcessingRef.current = true;
      setArticleQueue(prev => prev.map(item =>
        item.id === pending.id ? { ...item, status: 'processing' } : item
      ));

      try {
        const result = await ListsManager.analyzeArticleForReport(approvalId, pending.url, pending.listId);

        if (result.status === 'relevant' && result.post) {
          setArticleQueue(prev => prev.map(item =>
            item.id === pending.id ? { ...item, status: 'success' } : item
          ));
          addPostToReportData(result.post, pending.listId, pending.listName);
        } else {
          const statusMap = { not_relevant: 'not_relevant', duplicate: 'duplicate' };
          setArticleQueue(prev => prev.map(item =>
            item.id === pending.id ? { ...item, status: statusMap[result.status] || 'failed' } : item
          ));
        }
      } catch (err) {
        setArticleQueue(prev => prev.map(item =>
          item.id === pending.id ? { ...item, status: 'failed' } : item
        ));
      } finally {
        articleProcessingRef.current = false;
      }
    };

    processNext();
  }, [articleQueue, approvalId, addPostToReportData]);

  const toggleSection = useCallback((listId) => {
    setCollapsedSections(prev => ({ ...prev, [listId]: !prev[listId] }));
  }, []);

  if (loading) {
    return (
      <div className="dr-page">
        <style>{STYLES}</style>
        <div className="dr-loading">
          <div className="dr-spinner" />
          <div style={{ fontSize: '16px', color: '#64748b' }}>טוען...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dr-page">
        <style>{STYLES}</style>
        <div className="dr-loading">
          <div style={{ fontSize: '18px', color: '#dc2626', fontWeight: '600' }}>{error}</div>
          <div style={{ fontSize: '15px', color: '#64748b' }}>נסה שנית מאוחר יותר</div>
        </div>
      </div>
    );
  }

  if (!approvalId) {
    return (
      <div className="dr-page">
        <style>{STYLES}</style>
        <div className="dr-header">
          <h1 className="dr-header__title">דו״חות יומיים ממתינים</h1>
          <div className="dr-header__subtitle">בחר דו״ח לסקירה ושליחה</div>
        </div>
        <div className="dr-container">
          {pendingReports.length === 0 ? (
            <div className="dr-empty">
              <FileText size={48} color="#888" />
              <div style={{ color: '#888', fontSize: '16px' }}>אין דו״חות ממתינים</div>
            </div>
          ) : (
            pendingReports.map(report => (
              <div
                key={report.id}
                className="dr-report-card"
                onClick={() => navigate(`/daily-review/${report.id}`)}
              >
                <div className="dr-report-card__name">{report.org_name}</div>
                <div className="dr-report-card__meta">
                  {report.report_date} · {report.total_posts} אזכורים
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  if (!reportData) return null;

  const platformCounts = reportData.report_data?.platform_counts || {};
  const totalPosts = Object.values(platformCounts).reduce((sum, count) => sum + count, 0);
  const hourlyPostsByList = reportData.report_data?.hourly_posts_by_list || [];

  return (
    <div className="dr-page" style={{ paddingBottom: '100px' }}>
      <style>{STYLES}</style>

      <div className="dr-header">
        <h1 className="dr-header__title">סקירת דו״ח יומי — {reportData.org_name}</h1>
        <div className="dr-header__subtitle">
          {reportData.report_date} · {totalPosts} אזכורים
        </div>
        <div className="dr-countdown">
          <Clock size={16} color="#ffffff" />
          <span>הדו״ח יישלח רק לאחר אישור ידני — לחצו "שלח" כשמוכן</span>
        </div>
      </div>

      <div className="dr-container">
        <div className="dr-platform-stats">
          {Object.entries(PLATFORM_LABELS).map(([key, label]) => (
            <div key={key} className="dr-platform-stat">
              <div className="dr-platform-stat__icon">{PLATFORM_ICONS[key]}</div>
              <div className="dr-platform-stat__count">{platformCounts[key] || 0}</div>
              <div className="dr-platform-stat__label">{label}</div>
            </div>
          ))}
        </div>

        <Sentiment30DChart sentiment30d={sentiment30d} sentiment30dTotals={sentiment30dTotals} />

        <div className="dr-section">
          <div className="dr-section__header">
            <h2 className="dr-section__title">סיכום AI</h2>
            <button
              className="dr-btn-refresh"
              onClick={async () => {
                const result = await ListsManager.regeneratePresendSummary(approvalId);
                if (result?.summary_text) {
                  const bullets = Array.isArray(result.summary_text) ? result.summary_text : [result.summary_text];
                  setSummaryBullets(bullets);
                }
              }}
            >
              רענן סיכום
            </button>
          </div>
          <div className="dr-summary-box">
            {summaryBullets.map((bullet, index) => (
              <div key={index} className="dr-bullet">
                <div className="dr-bullet__dot" />
                <textarea
                  className={`dr-bullet__input ${focusedBulletIndex === index ? 'dr-bullet__input--focused' : ''}`}
                  value={bullet}
                  onChange={(e) => handleBulletChange(index, e.target.value)}
                  onFocus={() => setFocusedBulletIndex(index)}
                  onBlur={() => handleBulletBlur(index)}
                  placeholder="הקלד נקודה..."
                  dir="rtl"
                />
                <button
                  className="dr-bullet__remove"
                  onClick={() => handleRemoveBullet(index)}
                  title="הסר נקודה"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            <button className="dr-btn-add-bullet" onClick={handleAddBullet}>
              <Plus size={18} />
              <span>הוסף נקודה</span>
            </button>
          </div>
        </div>

        {connectTheDots && (
          <div className="dr-section">
            <h2 className="dr-section__title" style={{ marginBottom: '4px' }}>חיבור הנקודות</h2>
            <div className="dr-ctd-hint">תובנות מחברות-נקודות. ניתן לערוך הכול — השינויים נשמרים אוטומטית ויופיעו בדו״ח.</div>

            {/* Headline */}
            <div className="dr-ctd-block">
              <label className="dr-ctd-label">כותרת מובילה</label>
              <textarea
                className="dr-ctd-input"
                value={connectTheDots.headline || ''}
                onChange={(e) => updateCtdHeadline(e.target.value)}
                placeholder="כותרת מובילה..."
                dir="rtl"
              />
            </div>

            {/* Today events */}
            {(connectTheDots.today_events || []).map((ev, i) => (
              <div key={`ctd-ev-${i}`} className="dr-ctd-card">
                <button className="dr-ctd-remove" onClick={() => removeCtdEvent(i)} title="הסר אירוע"><X size={14} /></button>
                <input
                  className="dr-ctd-input dr-ctd-title"
                  value={ev.title || ''}
                  onChange={(e) => updateCtdEventField(i, 'title', e.target.value)}
                  placeholder="כותרת האירוע" dir="rtl"
                />
                {[['what', 'מה קרה'], ['why', 'למה'], ['past_link', 'הקשר לעבר'], ['brand_implication', 'משמעות']].map(([field, label]) => (
                  <div key={field} className="dr-ctd-field">
                    <span className="dr-ctd-field-label">{label}:</span>
                    <textarea
                      className="dr-ctd-input"
                      value={ev[field] || ''}
                      onChange={(e) => updateCtdEventField(i, field, e.target.value)}
                      placeholder={label} dir="rtl"
                    />
                  </div>
                ))}
              </div>
            ))}

            {/* Connections */}
            {(connectTheDots.connections || []).length > 0 && (
              <div className="dr-ctd-subhead">נקודות מרכזיות</div>
            )}
            {(connectTheDots.connections || []).map((c, i) => (
              <div key={`ctd-conn-${i}`} className="dr-ctd-card">
                <button className="dr-ctd-remove" onClick={() => removeCtdConnection(i)} title="הסר חיבור"><X size={14} /></button>
                <input
                  className="dr-ctd-input dr-ctd-title"
                  value={c.title || ''}
                  onChange={(e) => updateCtdConnectionField(i, 'title', e.target.value)}
                  placeholder="כותרת" dir="rtl"
                />
                <textarea
                  className="dr-ctd-input"
                  value={c.story || ''}
                  onChange={(e) => updateCtdConnectionField(i, 'story', e.target.value)}
                  placeholder="תיאור" dir="rtl"
                />
              </div>
            ))}
          </div>
        )}

        {hourlyPostsByList.map(section => {
          const isCollapsed = collapsedSections[section.list_id];
          const totalSectionPosts = (section.hours || []).reduce((sum, h) => sum + (h.posts || []).length, 0);

          return (
            <div key={section.list_id} className="dr-list-section">
              <div className="dr-list-section__header" onClick={() => toggleSection(section.list_id)}>
                <div className="dr-list-section__left">
                  <span className="dr-list-section__name">{section.list_name}</span>
                  <span className="dr-list-section__badge">{totalSectionPosts}</span>
                </div>
                {isCollapsed ? <ChevronDown size={20} color="#64748b" /> : <ChevronUp size={20} color="#64748b" />}
              </div>

              {!isCollapsed && (
                <div className="dr-list-section__body">
                  {(section.hours || []).map(hourGroup => (
                    <div key={hourGroup.label} className="dr-hour-group">
                      <div className="dr-hour-group__label">{hourGroup.label}</div>
                      <AnimatePresence>
                        {(hourGroup.posts || []).map(post => (
                          <PostCard
                            key={`${post.platform_id}-${post.source}`}
                            post={post}
                            orgLists={reportData.org_lists || []}
                            currentListId={section.list_id}
                            onRemove={handleRemovePost}
                            onMove={handleMovePost}
                            onUpdateField={handleUpdateField}
                            isRemoving={removingPosts[`${post.platform_id}-${post.source}`]}
                            movedListId={movedPosts[`${post.platform_id}-${post.source}`]}
                          />
                        ))}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <div className="dr-section dr-add-article-section">
          <div
            className="dr-section__header dr-add-article-toggle"
            onClick={() => setArticleSectionOpen(!articleSectionOpen)}
            style={{ cursor: 'pointer' }}
          >
            <h2 className="dr-section__title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Link size={18} />
              הוסף כתבה
            </h2>
            {articleSectionOpen ? <ChevronUp size={20} color="#64748b" /> : <ChevronDown size={20} color="#64748b" />}
          </div>

          {articleSectionOpen && (
            <div className="dr-add-article-body">
              <div className="dr-add-article-form">
                <input
                  type="url"
                  className="dr-add-article-input"
                  placeholder="הדבק קישור לכתבה..."
                  value={articleUrl}
                  onChange={(e) => setArticleUrl(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddArticle(); }}
                  dir="ltr"
                />
                <select
                  className="dr-add-article-select"
                  value={articleListId}
                  onChange={(e) => setArticleListId(e.target.value)}
                >
                  <option value="">בחר רשימה...</option>
                  {(reportData?.org_lists || []).map(list => (
                    <option key={list.id} value={list.id}>{list.name}</option>
                  ))}
                </select>
                <button
                  className="dr-btn dr-btn--add-article"
                  onClick={handleAddArticle}
                  disabled={!articleUrl.trim() || !articleListId}
                >
                  <Plus size={16} />
                  <span>הוסף</span>
                </button>
              </div>

              {articleQueue.length > 0 && (
                <div className="dr-article-queue">
                  {articleQueue.map(item => (
                    <div key={item.id} className={`dr-article-queue-item dr-article-queue-item--${item.status}`}>
                      <div className="dr-article-queue-item__icon">
                        {item.status === 'pending' && <Clock size={14} color="#94a3b8" />}
                        {item.status === 'processing' && <Loader size={14} className="dr-spin" color="#3b82f6" />}
                        {item.status === 'success' && <Check size={14} color="#16a34a" />}
                        {item.status === 'duplicate' && <AlertCircle size={14} color="#d97706" />}
                        {(item.status === 'failed' || item.status === 'not_relevant') && <AlertCircle size={14} color="#dc2626" />}
                      </div>
                      <div className="dr-article-queue-item__info">
                        <span className="dr-article-queue-item__url" dir="ltr">{item.url.length > 50 ? item.url.substring(0, 50) + '...' : item.url}</span>
                        <span className="dr-article-queue-item__list">{item.listName}</span>
                      </div>
                      <div className="dr-article-queue-item__status">
                        {item.status === 'pending' && 'ממתין'}
                        {item.status === 'processing' && 'מנתח...'}
                        {item.status === 'success' && 'נוסף בהצלחה'}
                        {item.status === 'duplicate' && 'כבר קיים'}
                        {item.status === 'not_relevant' && 'לא רלוונטי'}
                        {item.status === 'failed' && 'נכשל'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="dr-action-bar">
        {removedCount > 0 && (
          <div className="dr-action-bar__removed">
            <Trash2 size={16} color="#dc2626" />
            <span>{removedCount} הוסרו</span>
          </div>
        )}
        <div className="dr-action-bar__buttons">
          <button className="dr-btn dr-btn--preview" onClick={handlePreviewReport} disabled={previewingReport}>
            {previewingReport ? (
              <div className="dr-spinner dr-spinner--sm" />
            ) : (
              <>
                <Eye size={18} />
                <span>תצוגה מקדימה</span>
              </>
            )}
          </button>
          <button className="dr-btn dr-btn--send" onClick={handleSendReport} disabled={sendingReport}>
            {sendingReport ? (
              <div className="dr-spinner dr-spinner--sm dr-spinner--white" />
            ) : (
              <>
                <Send size={18} />
                <span>שלח דו״ח</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Web preview — renders the member-facing report page inside a modal, using the
          same component the public /r/{token} viewer uses, so what you approve here is
          literally what gets emailed. */}
      {previewData && (
        <div className="dr-preview-overlay" onClick={() => setPreviewData(null)}>
          <style>{REPORT_STYLES}</style>
          <div className="dr-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dr-preview-modal__bar">
              <span className="dr-preview-modal__title">
                תצוגה מקדימה — כך ייראה הדו״ח לחברי הארגון
              </span>
              <button
                className="dr-preview-modal__close"
                onClick={() => setPreviewData(null)}
                aria-label="סגור תצוגה מקדימה"
              >
                <X size={18} />
              </button>
            </div>
            <div className="dr-preview-modal__body">
              <DailyReportView reportData={previewData} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const STYLES = `
.dr-preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.dr-preview-modal {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 1100px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
}

.dr-preview-modal__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  flex: 0 0 auto;
}

.dr-preview-modal__title {
  font-size: 13px;
  font-weight: 700;
  color: #334155;
}

.dr-preview-modal__close {
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  display: flex;
  padding: 4px;
}

/* The report view manages its own internal scrolling (.rp-detail). */
.dr-preview-modal__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

@import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap');

@keyframes spin {
  to { transform: rotate(360deg); }
}

.dr-page {
  min-height: 100vh;
  background-color: #f5ede4;
  direction: rtl;
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
}

.dr-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  min-height: 100vh;
  flex-direction: column;
  gap: 16px;
}

.dr-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.dr-spinner--sm {
  width: 18px;
  height: 18px;
  border-width: 2px;
  border-color: #2d334a;
  border-top-color: transparent;
}

.dr-spinner--white {
  border-color: #ffffff;
  border-top-color: transparent;
}

.dr-header {
  background: linear-gradient(135deg, #17365d 0%, #1e4a7a 100%);
  padding: 24px;
  padding-bottom: 16px;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 12px rgba(0,0,0,0.15);
}

.dr-header__title {
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 8px 0;
}

.dr-header__subtitle {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 12px;
}

.dr-countdown {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  gap: 6px;
  background-color: rgba(255, 255, 255, 0.15);
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
}

.dr-countdown--urgent {
  background-color: rgba(239, 68, 68, 0.9);
}

.dr-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px 16px;
}

.dr-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 60px;
  gap: 12px;
}

.dr-report-card {
  background-color: #ffffff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  cursor: pointer;
  transition: all 0.2s ease;
}

.dr-report-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transform: translateY(-2px);
}

.dr-report-card__name {
  font-size: 18px;
  font-weight: 700;
  color: #17365d;
  margin-bottom: 4px;
}

.dr-report-card__meta {
  font-size: 14px;
  color: #5a6178;
}

/* Platform stats row */
.dr-platform-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-bottom: 24px;
}

.dr-platform-stat {
  background-color: #ffffff;
  border-radius: 12px;
  padding: 14px 18px;
  min-width: 90px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.dr-platform-stat__icon {
  margin-bottom: 4px;
}

.dr-platform-stat__count {
  font-size: 28px;
  font-weight: 700;
  color: #2d334a;
}

.dr-platform-stat__label {
  font-size: 13px;
  color: #64748b;
}

/* Sections */
.dr-section {
  margin-bottom: 24px;
}

.dr-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.dr-section__title {
  font-size: 20px;
  font-weight: 700;
  color: #2d334a;
  margin: 0;
}

.dr-btn-refresh {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 6px;
  background-color: rgba(59,130,246,0.1);
  border: none;
  cursor: pointer;
  font-size: 12px;
  color: #3b82f6;
  font-weight: 600;
  font-family: inherit;
}

.dr-btn-refresh:hover {
  background-color: rgba(59,130,246,0.2);
}

/* Summary bullets */
.dr-summary-box {
  background-color: #ffffff;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dr-bullet {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  position: relative;
}

.dr-bullet__dot {
  width: 8px;
  height: 8px;
  border-radius: 4px;
  background-color: #3b82f6;
  margin-top: 10px;
  flex-shrink: 0;
}

.dr-bullet__input {
  flex: 1;
  font-size: 15px;
  color: #2d334a;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid transparent;
  background-color: transparent;
  outline: none;
  font-family: inherit;
  resize: vertical;
  min-height: 40px;
  direction: rtl;
  transition: all 0.15s ease;
}

.dr-bullet__input--focused {
  border-color: #3b82f6;
  background-color: #f8fafc;
}

.dr-bullet__remove {
  position: absolute;
  left: 0;
  top: 6px;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  border: none;
  background-color: transparent;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.dr-bullet:hover .dr-bullet__remove {
  opacity: 1;
}

.dr-bullet__remove:hover {
  background-color: #fee2e2;
  color: #dc2626;
}

.dr-btn-add-bullet {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  background-color: #f1f5f9;
  border: none;
  cursor: pointer;
  align-self: flex-start;
  font-family: inherit;
  font-size: 14px;
  color: #3b82f6;
  font-weight: 600;
}

.dr-btn-add-bullet:hover {
  background-color: #e2e8f0;
}

/* List sections */
.dr-list-section {
  background-color: #f0e8df;
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
}

.dr-list-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.dr-list-section__header:hover {
  background-color: #ebe3d9;
}

.dr-list-section__left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dr-list-section__name {
  font-size: 18px;
  font-weight: 700;
  color: #2d334a;
}

.dr-list-section__badge {
  background-color: #3b82f6;
  border-radius: 12px;
  padding: 3px 10px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
}

.dr-list-section__body {
  padding: 0 16px 16px;
}

.dr-hour-group {
  padding-top: 16px;
  border-top: 1px solid rgba(0,0,0,0.06);
}

.dr-hour-group__label {
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 12px;
}

/* Post cards */
.post-card {
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 12px;
  position: relative;
  transition: box-shadow 0.2s ease;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}

.post-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.post-card--twitter {
  background-color: #ffffff;
  border: 1px solid #cfd9de;
}

.post-card--tiktok {
  background-color: #ffffff;
  border: 1px solid #fee2e2;
  border-right: 3px solid #fe2c55;
}

.post-card--instagram {
  background-color: #ffffff;
  border: 1px solid #fce7f3;
  overflow: hidden;
}

.post-card--telegram {
  background-color: #edf7ff;
  border: 1px solid #bae6fd;
  border-right: 3px solid #34aadf;
  border-radius: 10px 4px 10px 10px;
}

.post-card--rss {
  background-color: #ffffff;
  border: 1px solid #fed7aa;
  border-right: 3px solid #ff6600;
}

.post-card--webz {
  background-color: #ffffff;
  border: 1px solid #bfdbfe;
  border-right: 3px solid #1a73e8;
}

.post-card--facebook {
  background-color: #f0e8df;
  border: 1px solid #e2e8f0;
}

.post-card__ig-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888);
  border-radius: 10px 10px 0 0;
}

.post-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.post-card__author {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.post-card__avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.post-card__avatar-fallback {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  color: #ffffff;
  flex-shrink: 0;
}

.post-card__author-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.post-card__name {
  font-size: 14px;
  font-weight: 600;
  color: #2d334a;
  direction: ltr;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.post-card__handle {
  font-size: 12px;
  color: #64748b;
  direction: ltr;
  text-align: right;
}

.post-card__followers {
  font-size: 11px;
  color: #94a3b8;
  white-space: nowrap;
  padding: 2px 8px;
  background-color: #f1f5f9;
  border-radius: 10px;
}

.post-card__platform-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.post-card__actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.post-card__btn {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.post-card__btn--link {
  background-color: #f0fdf4;
  color: #16a34a;
  text-decoration: none;
}

.post-card__btn--link:hover {
  background-color: #dcfce7;
}

.post-card__btn--move {
  background-color: #f1f5f9;
  color: #3b82f6;
}

.post-card__btn--move:hover {
  background-color: #dbeafe;
}

.post-card__btn--remove {
  background-color: #fef2f2;
  color: #dc2626;
}

.post-card__btn--remove:hover {
  background-color: #fee2e2;
}

.post-card__dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  background-color: #ffffff;
  border-radius: 8px;
  margin-top: 4px;
  min-width: 180px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 1000;
  overflow: hidden;
}

.post-card__dropdown-item {
  width: 100%;
  padding: 12px;
  background-color: transparent;
  border: none;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  text-align: right;
  font-size: 14px;
  color: #2d334a;
  font-family: inherit;
}

.post-card__dropdown-item:hover {
  background-color: #f8fafc;
}

.post-card__link {
  text-decoration: none;
  color: inherit;
  display: block;
  cursor: pointer;
}

.post-card__link:hover .post-card__content {
  color: #1d4ed8;
}

.post-card__content {
  font-size: 14px;
  line-height: 22px;
  margin-bottom: 12px;
  color: #2d334a;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
}

.post-card__media {
  margin-bottom: 12px;
  border-radius: 8px;
  overflow: hidden;
}

.post-card__media img {
  width: 100%;
  max-height: 200px;
  object-fit: cover;
  display: block;
}

.post-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.post-card__stats {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.post-card__stat {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #64748b;
}

.post-card__time {
  font-size: 12px;
  color: #94a3b8;
}

.post-card__sentiment {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.post-card__sentiment-input {
  width: 64px;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #cbd5e1;
  outline: none;
  font-family: inherit;
  text-align: center;
}

.post-card__sentiment-input:focus {
  border-color: #3b82f6;
}

.post-card__insight {
  margin-top: 12px;
  padding: 10px 12px;
  background-color: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.post-card__insight--clickable {
  cursor: pointer;
  transition: border-color 0.15s ease;
}

.post-card__insight--clickable:hover {
  border-color: #cbd5e1;
}

.post-card__insight-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
}

.post-card__insight-text {
  font-size: 13px;
  color: #2d334a;
  line-height: 20px;
}

/* ── Tweet dependency embeds (reply / quote parents) ── */
.post-card__deps {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  direction: rtl;
}

.post-dep {
  border: 1px solid #cfd9de;
  border-radius: 12px;
  background: #f7f9f9;
  padding: 8px 10px;
  direction: rtl;
  text-align: right;
}

.post-dep__rel {
  font-size: 10px;
  color: #536471;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
}

.post-dep__rel svg { color: #536471; }

.post-dep__head {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 3px;
}

.post-dep__avatar,
.post-dep__avatar-img {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  flex-shrink: 0;
}

.post-dep__avatar {
  background: #1d9bf0;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.post-dep__avatar-img { object-fit: cover; }

.post-dep__name {
  font-size: 12px;
  font-weight: 700;
  color: #0f1419;
}

.post-dep__handle {
  font-size: 11px;
  color: #536471;
}

.post-dep__body {
  font-size: 12.5px;
  line-height: 1.45;
  color: #0f1419;
  white-space: pre-wrap;
  word-break: break-word;
}

.post-dep__body[dir="ltr"] { text-align: left; }

.post-dep__open {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
  padding: 4px 11px;
  background: #0f1419;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  text-decoration: none;
  direction: rtl;
}

.post-card__insight-input {
  width: 100%;
  font-size: 13px;
  color: #2d334a;
  outline: none;
  min-height: 50px;
  border: none;
  background-color: transparent;
  font-family: inherit;
  resize: vertical;
  line-height: 20px;
}

/* Action bar */
.dr-action-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background-color: #ffffff;
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -2px 8px rgba(0,0,0,0.06);
  z-index: 200;
}

.dr-action-bar__removed {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 20px;
  background-color: #fef2f2;
  font-size: 14px;
  font-weight: 600;
  color: #dc2626;
}

.dr-action-bar__buttons {
  display: flex;
  gap: 12px;
  margin-right: auto;
}

.dr-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 10px;
  cursor: pointer;
  border: none;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  transition: all 0.15s ease;
}

.dr-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.dr-btn--preview {
  background-color: #f1f5f9;
  color: #2d334a;
}

.dr-btn--preview:hover:not(:disabled) {
  background-color: #e2e8f0;
}

.dr-btn--send {
  background-color: #10b981;
  color: #ffffff;
}

.dr-btn--send:hover:not(:disabled) {
  background-color: #059669;
}

.dr-add-article-section {
  margin-top: 16px;
}

.dr-add-article-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dr-add-article-body {
  padding: 16px 0 0;
}

.dr-add-article-form {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.dr-add-article-input {
  flex: 1;
  min-width: 200px;
  padding: 10px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s;
}

.dr-add-article-input:focus {
  border-color: #3b82f6;
}

.dr-add-article-select {
  padding: 10px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  background: #fff;
  min-width: 140px;
  outline: none;
  direction: rtl;
}

.dr-add-article-select:focus {
  border-color: #3b82f6;
}

.dr-btn--add-article {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 16px;
  background-color: #3b82f6;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s;
}

.dr-btn--add-article:hover:not(:disabled) {
  background-color: #2563eb;
}

.dr-btn--add-article:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.dr-article-queue {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.dr-article-queue-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.dr-article-queue-item--success {
  border-color: #bbf7d0;
  background: #f0fdf4;
}

.dr-article-queue-item--failed,
.dr-article-queue-item--not_relevant {
  border-color: #fecaca;
  background: #fef2f2;
}

.dr-article-queue-item--duplicate {
  border-color: #fde68a;
  background: #fffbeb;
}

.dr-article-queue-item--processing {
  border-color: #bfdbfe;
  background: #eff6ff;
}

.dr-article-queue-item__icon {
  flex-shrink: 0;
}

.dr-article-queue-item__info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.dr-article-queue-item__url {
  font-size: 12px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dr-article-queue-item__list {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.dr-article-queue-item__status {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  flex-shrink: 0;
}

@keyframes dr-spin {
  to { transform: rotate(360deg); }
}

.dr-spin {
  animation: dr-spin 1s linear infinite;
}

/* ── Connect-the-dots editor ── */
.dr-ctd-hint { font-size: 12px; color: #94a3b8; margin-bottom: 14px; }
.dr-ctd-block { margin-bottom: 14px; }
.dr-ctd-label { display: block; font-size: 12px; font-weight: 700; color: #475569; margin-bottom: 4px; }
.dr-ctd-subhead { font-size: 14px; font-weight: 700; color: #17365d; margin: 18px 0 8px; }
.dr-ctd-card { position: relative; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px 14px; margin-bottom: 10px; }
.dr-ctd-field { display: flex; gap: 6px; align-items: flex-start; margin-top: 6px; }
.dr-ctd-field-label { flex-shrink: 0; font-size: 12px; font-weight: 700; color: #0a84ff; padding-top: 9px; min-width: 64px; }
.dr-ctd-input { width: 100%; border: 1px solid #e2e8f0; border-radius: 8px; padding: 7px 10px; font-size: 13px;
  font-family: inherit; color: #0f172a; resize: vertical; min-height: 36px; line-height: 1.5; background: #fff; }
.dr-ctd-input:focus { outline: none; border-color: #0a84ff; box-shadow: 0 0 0 2px rgba(10,132,255,0.12); }
textarea.dr-ctd-input { min-height: 38px; }
.dr-ctd-title { font-weight: 700; margin-bottom: 4px; }
.dr-ctd-remove { position: absolute; top: 8px; left: 8px; background: #fee2e2; color: #dc2626; border: none;
  border-radius: 6px; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; cursor: pointer; }
.dr-ctd-remove:hover { background: #fecaca; }
.dr-ctd-inf-row { position: relative; display: flex; gap: 6px; align-items: center; margin-bottom: 6px; padding-left: 32px; }
.dr-ctd-remove--inline { position: absolute; top: 50%; transform: translateY(-50%); }
.dr-ctd-inf-name { flex: 2; }
.dr-ctd-inf-sm { flex: 1; min-width: 0; }
`;

export default DailyReviewScreen;
